import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Package, Eye, Star, UserPlus, UserCheck, ChevronLeft, Truck, Hash, ExternalLink, Flag, Lock, MessageCircle, X, ChevronRight, ZoomIn, TimerOff } from 'lucide-react';
import PriceCountdown from '../components/listing/PriceCountdown';
import ShareButtons from '../components/listing/ShareButtons';
import QASection from '../components/listing/QASection';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { formatDate, formatPrice, getCustomerBadges, getRoleBadgeInfo } from '../utils/formatters';
import { listingService } from '../services/listingService';
import { followService } from '../services/followService';
import { useLanguage } from '../context/LanguageContext';
import { useCountdown } from '../hooks/useCountdown';

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const { t, tCond } = useLanguage();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [following, setFollowing] = useState(false);
  const [reserved, setReserved] = useState(false);
  const [showReserveConfirm, setShowReserveConfirm] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  // Hook'lar erken return'den önce tanımlanmalı
  const { remaining } = useCountdown(listing?.nextDropAt ?? null);
  // Yalnızca expiresAt varsa ve geçmişse expired
  const isExpired = listing?.expiresAt
    ? new Date(listing.expiresAt).getTime() <= Date.now()
    : false;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await listingService.getById(id);
        setListing(data);
        setReserved(data.isReserved || false);
      } catch {
        setListing(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  useEffect(() => {
    if (listing?.seller?.id && user && listing.seller.id !== user.id) {
      followService.isFollowing(listing.seller.id).then(setFollowing).catch(() => {});
    }
  }, [listing, user]);

  // Lightbox klavye kontrolü + body scroll kilidi
  useEffect(() => {
    if (!lightbox) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape')     setLightbox(false);
      if (e.key === 'ArrowRight') setActiveImg(i => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')  setActiveImg(i => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  const handleReserve = async () => {
    if (!user) { navigate('/login'); return; }
    setReserving(true);
    try {
      await listingService.reserve(listing.id);
      setReserved(true);
      setShowReserveConfirm(false);
      addNotification({ type: 'reservation', message: `"${listing.title.slice(0, 30)}..." rezerve edildi!`, listingId: listing.id });
    } catch (err) {
      alert(err?.response?.data?.message || 'Rezervasyon başarısız.');
    } finally {
      setReserving(false);
    }
  };

  const handleFollow = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (following) {
        await followService.unfollow(listing.seller.id);
        setFollowing(false);
      } else {
        await followService.follow(listing.seller.id);
        setFollowing(true);
      }
    } catch {}
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-20 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-apple-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <p className="text-2xl font-semibold text-apple-gray-dark mb-4">{t('listing_detail.not_found')}</p>
      <Link to="/" className="btn-primary">{t('not_found.home')}</Link>
    </div>
  );

  const { title, images = [], description, condition, city, district, seller = {}, viewCount, shippingCost, tags = [], listingNo, createDate } = listing;
  const customerBadges = getCustomerBadges(seller.badges || []);
  const roleBadge = getRoleBadgeInfo(seller.role);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 animate-fade-in">
      <div className="flex items-center gap-2 text-sm text-apple-gray-mid mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 hover:text-apple-gray-dark transition-colors">
          <ChevronLeft className="w-4 h-4" /> {t('listing_detail.back')}
        </button>
        <span>/</span>
        <Link to="/search" className="hover:text-apple-gray-dark transition-colors">{t('footer.listings')}</Link>
        <span>/</span>
        <span className="text-apple-gray-dark truncate max-w-xs">{title}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Images */}
        <div>
          {/* Ana görsel — tıklanabilir */}
          <div
            className={`relative aspect-[4/3] rounded-3xl overflow-hidden bg-apple-gray mb-3 shadow-apple group cursor-zoom-in ${isExpired ? 'opacity-60' : ''}`}
            onClick={() => images.length > 0 && setLightbox(true)}
          >
            {isExpired && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <span className="flex items-center gap-2 bg-black/60 text-white text-sm font-semibold px-4 py-2 rounded-full backdrop-blur-sm">
                  <TimerOff className="w-4 h-4" /> {t('price_countdown.expired_short')}
                </span>
              </div>
            )}
            {images.length > 0
              ? <img src={images[activeImg]?.url || images[activeImg]} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              : <div className="w-full h-full flex items-center justify-center text-apple-gray-mid">Fotoğraf yok</div>
            }
            {images.length > 0 && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 backdrop-blur-sm text-white rounded-full p-2.5">
                  <ZoomIn className="w-5 h-5" />
                </div>
              </div>
            )}
            {/* Sol / Sağ ok — birden fazla görsel varsa */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeImg ? 'bg-white' : 'bg-white/50'}`} />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail listesi */}
          {images.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-colors ${activeImg === i ? 'border-apple-blue' : 'border-gray-200 hover:border-gray-400'}`}>
                  <img src={img?.url || img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 p-5 bg-apple-gray rounded-2xl">
            <ShareButtons listing={listing} />
          </div>

          <div className="mt-6">
            <QASection listing={{ ...listing, expired: isExpired }} />
          </div>
        </div>

        {/* Right: Details */}
        <div className="space-y-5">
          <div>
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-apple-gray-dark tracking-tight leading-tight flex-1">
                {title}
              </h1>
              <button className="p-2 rounded-full hover:bg-apple-gray transition-colors text-apple-gray-mid hover:text-apple-red">
                <Flag className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-apple-gray-mid">
              <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {(viewCount || 0).toLocaleString('de-DE')} {t('listing_detail.views')}</span>
              <span className="flex items-center gap-1"><Hash className="w-4 h-4" /> {listingNo}</span>
              {city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {city}{district ? `, ${district}` : ''}</span>}
              {condition && <span className="flex items-center gap-1"><Package className="w-4 h-4" /> {tCond(condition)}</span>}
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {tags.map(tag => (
                  <Link key={tag} to={`/search?q=${tag}`} className="text-xs bg-apple-gray text-apple-gray-mid px-2.5 py-1 rounded-full hover:bg-gray-200 transition-colors">
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <PriceCountdown listing={{ ...listing, reserved }} />

          <div className="flex gap-3">
            {isExpired ? (
              <div className="flex-1 bg-gray-100 border border-gray-200 rounded-full py-3.5 text-center text-gray-400 font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed select-none">
                <TimerOff className="w-4 h-4" /> {t('price_countdown.expired_short')}
              </div>
            ) : !reserved ? (
              <button
                onClick={() => setShowReserveConfirm(true)}
                className="flex-1 btn-primary py-3.5 text-base font-semibold flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4" /> {t('listing_detail.reserve')}
              </button>
            ) : (
              <div className="flex-1 bg-orange-50 border border-orange-200 rounded-full py-3.5 text-center text-orange-700 font-semibold text-sm flex items-center justify-center gap-2">
                <Lock className="w-4 h-4" /> {t('listing_detail.reserved')}
              </div>
            )}
            <button
              disabled={isExpired}
              className="btn-secondary py-3.5 px-5 text-base flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          </div>

          {/* ── Lightbox (Portal → document.body) ── */}
          {lightbox && createPortal(
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                width: '100%',
                height: '100%',
                // Sitenin kendisi blurlu arka plan olarak görünsün
                backdropFilter: 'blur(48px) brightness(0.45) saturate(1.6)',
                WebkitBackdropFilter: 'blur(48px) brightness(0.45) saturate(1.6)',
                background: 'rgba(209, 213, 219, 0.18)',
              }}
            >
              {/* ── Üst Bar ── */}
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  backdropFilter: 'blur(32px) saturate(2)',
                  WebkitBackdropFilter: 'blur(32px) saturate(2)',
                  background: 'rgba(255,255,255,0.18)',
                  borderBottom: '1px solid rgba(255,255,255,0.28)',
                }}
                className="flex items-center justify-between px-5 py-3 w-full"
              >
                {/* Sol: küçük thumbnail + başlık */}
                <div className="flex items-center gap-2 min-w-0">
                  {images[activeImg] && (
                    <img
                      src={images[activeImg]?.url || images[activeImg]}
                      alt=""
                      className="w-7 h-7 rounded-lg object-cover shrink-0 hidden sm:block"
                      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }}
                    />
                  )}
                  <span
                    className="text-sm font-medium truncate select-none"
                    style={{ color: 'rgba(255,255,255,0.88)' }}
                  >
                    {title.length > 32 ? title.slice(0, 32) + '…' : title}
                  </span>
                </div>

                {/* Sağ: sayaç + kapat */}
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {images.length > 1 && (
                    <span
                      className="text-xs font-medium tabular-nums select-none px-2.5 py-1 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.16)', color: 'rgba(255,255,255,0.70)' }}
                    >
                      {activeImg + 1}/{images.length}
                    </span>
                  )}
                  <button
                    onClick={() => setLightbox(false)}
                    style={{
                      background: 'rgba(255,255,255,0.20)',
                      border: '1px solid rgba(255,255,255,0.30)',
                      color: 'rgba(255,255,255,0.9)',
                    }}
                    className="h-7 px-3 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-white/30 active:scale-95 transition-all select-none"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{t('listing_detail.cancel')}</span>
                  </button>
                </div>
              </div>

              {/* ── Görsel ── */}
              <div
                className="relative flex items-center justify-center overflow-hidden"
                onClick={() => setLightbox(false)}
                style={{ padding: '12px' }}
              >
                <img
                  key={activeImg}
                  src={images[activeImg]?.url || images[activeImg]}
                  alt={title}
                  draggable={false}
                  onClick={e => e.stopPropagation()}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: '14px',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.10)',
                    animation: 'lbFadeIn .22s cubic-bezier(.4,0,.2,1)',
                    userSelect: 'none',
                  }}
                />

                {/* Sol ok — resmin üzerinde, sadece birden fazla görsel varsa */}
                {images.length > 1 && (
                  <button
                    onClick={e => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}
                    style={{
                      background: 'rgba(255,255,255,0.20)',
                      border: '1px solid rgba(255,255,255,0.30)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      color: 'rgba(255,255,255,0.95)',
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-white/30 active:scale-95 transition-all"
                  >
                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}

                {/* Sağ ok */}
                {images.length > 1 && (
                  <button
                    onClick={e => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}
                    style={{
                      background: 'rgba(255,255,255,0.20)',
                      border: '1px solid rgba(255,255,255,0.30)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      color: 'rgba(255,255,255,0.95)',
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-white/30 active:scale-95 transition-all"
                  >
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>

              {/* ── Alt Bar (thumbnail strip) ── */}
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  backdropFilter: 'blur(32px) saturate(2)',
                  WebkitBackdropFilter: 'blur(32px) saturate(2)',
                  background: 'rgba(255,255,255,0.14)',
                  borderTop: '1px solid rgba(255,255,255,0.22)',
                }}
                className="flex items-center justify-center gap-2 px-5 py-3 w-full overflow-x-auto"
              >
                {images.length > 1 ? images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    style={{
                      outline: i === activeImg ? '2px solid rgba(255,255,255,0.95)' : '2px solid transparent',
                      outlineOffset: '2px',
                      opacity: i === activeImg ? 1 : 0.5,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      transition: 'all .18s',
                      flexShrink: 0,
                    }}
                    className="w-10 h-10 hover:opacity-80"
                  >
                    <img src={img?.url || img} alt="" className="w-full h-full object-cover" />
                  </button>
                )) : (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.9)' }} />
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

          {showReserveConfirm && createPortal(
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] flex items-center justify-center p-4">
              <div className="bg-white rounded-3xl shadow-apple-lg p-8 max-w-sm w-full animate-slide-up">
                <h3 className="text-xl font-bold text-apple-gray-dark mb-2">{t('listing_detail.reserve_confirm_title')}</h3>
                <p className="text-apple-gray-mid text-sm mb-2">
                  <strong>{title.slice(0, 50)}</strong> — <strong className="text-apple-gray-dark">{formatPrice(listing.currentPrice)}</strong>
                </p>
                <p className="text-xs text-apple-gray-mid mb-6 bg-orange-50 p-3 rounded-xl">
                  {t('listing_detail.reserve_confirm_msg')}
                </p>
                <div className="flex gap-3">
                  <button onClick={handleReserve} disabled={reserving} className="flex-1 btn-primary py-3 disabled:opacity-60">
                    {reserving ? t('listing_detail.reserving') : t('listing_detail.confirm')}
                  </button>
                  <button onClick={() => setShowReserveConfirm(false)} className="flex-1 btn-secondary py-3">{t('listing_detail.cancel')}</button>
                </div>
              </div>
            </div>,
            document.body
          )}

          <div className="flex items-center gap-3 p-4 bg-apple-gray rounded-2xl">
            <Truck className="w-5 h-5 text-apple-gray-mid" />
            <div>
              <p className="text-sm font-medium text-apple-gray-dark">{t('listing_detail.shipping')}</p>
              <p className="text-sm text-apple-gray-mid">
                {shippingCost === null || shippingCost === undefined ? t('listing_detail.no_shipping') :
                  shippingCost === 0 ? t('listing_detail.free_shipping') :
                    formatPrice(shippingCost)}
              </p>
            </div>
          </div>

          {seller.id && (
            <div className="border border-gray-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-apple-gray-dark">{t('listing_detail.seller')}</h3>
                <Link to={`/profile/${seller.id}`} className="text-xs text-apple-blue hover:underline flex items-center gap-1">
                  {t('profile.listings')} <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-apple-blue flex items-center justify-center text-white font-bold text-lg">
                  {(seller.userNameSurname || seller.name || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-apple-gray-dark">{seller.userNameSurname || seller.name}</span>
                    {seller.isVerified && <span className="text-apple-blue text-sm" title={t('badges.verified')}>✓</span>}
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${roleBadge.className}`}>{t(roleBadge.labelKey)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-xs text-apple-gray-mid">
                      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" /> {seller.rating || 0}
                    </span>
                    <span className="text-xs text-apple-gray-mid">{seller.totalSales || 0} {t('profile.sales_label')}</span>
                  </div>
                  {customerBadges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {customerBadges.map(b => (
                        <span key={b.labelKey} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${b.className}`}>{b.icon} {t(b.labelKey)}</span>
                      ))}
                    </div>
                  )}
                </div>
                {user && user.id !== seller.id && (
                  <button
                    onClick={handleFollow}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${following ? 'bg-apple-blue text-white' : 'border border-apple-blue text-apple-blue hover:bg-apple-blue hover:text-white'}`}
                  >
                    {following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                    {following ? t('listing_detail.unfollow') : t('listing_detail.follow')}
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <p className="text-sm text-apple-gray-mid leading-relaxed whitespace-pre-wrap">{description}</p>
          </div>

          <div className="text-xs text-apple-gray-mid pt-3 border-t border-gray-100 space-y-1">
            <p>#{listingNo}</p>
            <p>{formatDate(createDate)}</p>
            {city && <p className="flex items-center gap-1"><span className="font-medium text-apple-gray-dark">{city}{district ? `, ${district}` : ''}</span></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
